import { useState, useEffect, useCallback } from "react";
import { useLanguage } from "@/contexts/LanguageContext";
import axios from "axios";
import {
  Rss, Plus, Trash2, Pencil, Download, Eye, Sparkles,
  Loader2, CheckCircle2, XCircle, Clock, ExternalLink,
  FileText, Star, ArrowRight, RefreshCw, Wifi, Filter, Wand2,
  Globe, PenTool, ImageIcon
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const actionBadge = {
  publish: "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400",
  rewrite: "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400",
  skip: "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500",
};

export default function RssFeeds() {
  const { t } = useLanguage();
  const [feeds, setFeeds] = useState([]);
  const [pendingPosts, setPendingPosts] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("feeds");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingFeed, setEditingFeed] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [testing, setTesting] = useState(false);
  const [importing, setImporting] = useState(null);
  const [ratingId, setRatingId] = useState(null);
  const [selectedPending, setSelectedPending] = useState(null);
  const [pendingFilter, setPendingFilter] = useState("all");
  const [suggestingSite, setSuggestingSite] = useState(false);
  const [aiSuggestion, setAiSuggestion] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [autoPubFeed, setAutoPubFeed] = useState(null);
  const [publishSiteId, setPublishSiteId] = useState("");
  const [publishCatId, setPublishCatId] = useState("");
  const [publishCats, setPublishCats] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);
  const [publishing, setPublishing] = useState(false);

  // Dynamicznie laduj kategorie WP po wyborze strony
  const loadWpCategories = async (siteId) => {
    if (!siteId) { setPublishCats([]); setPublishCatId(""); return; }
    setLoadingCats(true);
    try {
      const res = await axios.get(`${API}/sites/wp/${siteId}/wp-categories`);
      const cats = res.data || [];
      setPublishCats(cats);
      // AI sugeruje kategorie na podstawie tytulu artykulu
      if (cats.length > 0 && selectedPending) {
        const title = (selectedPending.ai_rating?.title_pl || selectedPending.title || "").toLowerCase();
        const summary = (selectedPending.ai_rating?.summary_pl || selectedPending.summary || "").toLowerCase();
        const text = title + " " + summary;
        // Prosta heurystyka - szukaj dopasowania po nazwie kategorii
        let bestCat = cats[0];
        let bestScore = 0;
        for (const cat of cats) {
          const catName = cat.name.toLowerCase();
          const words = catName.split(/[\s,/]+/);
          let score = 0;
          for (const w of words) {
            if (w.length > 2 && text.includes(w)) score += 1;
          }
          if (score > bestScore) { bestScore = score; bestCat = cat; }
        }
        setPublishCatId(String(bestCat.id));
      }
    } catch (err) {
      setPublishCats([]);
    } finally {
      setLoadingCats(false);
    }
  };

  const handleApproveAndPublish = async (postId) => {
    if (!publishSiteId) {
      toast.error("Wybierz strone docelowa!");
      return;
    }
    setPublishing(true);
    try {
      const res = await axios.post(`${API}/rss-feeds/pending/${postId}/approve-and-generate`, {
        target_site_id: publishSiteId,
        wp_category_id: publishCatId ? parseInt(publishCatId) : undefined,
        wp_category: publishCats.find(c => String(c.id) === publishCatId)?.name || "",
      });
      toast.success("Artykul zatwierdzony! Generowanie i publikacja w tle.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad publikacji");
    } finally {
      setPublishing(false);
    }
  };
  const [runningAuto, setRunningAuto] = useState(null);

  const handleRunAutoNow = async (feedId, feedName) => {
    setRunningAuto(feedId);
    try {
      const res = await axios.post(`${API}/rss-feeds/${feedId}/run-auto-now`);
      toast.success(`Auto-publish uruchomiony dla ${feedName}! Import + ocena + publikacja w tle.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Blad uruchomienia");
    } finally {
      setTimeout(() => { setRunningAuto(null); fetchData(); }, 5000);
    }
  };
  const [form, setForm] = useState({
    name: "", url: "", auto_import: false, auto_publish: false, target_mode: "fixed", target_site_ids: [], check_interval: 60, site_id: "", category: "", source_language: "pl", source_type: "rss", import_limit: 10,
  });
  
  // Import posta FB
  const [fbDialogOpen, setFbDialogOpen] = useState(false);
  const [fbPostUrl, setFbPostUrl] = useState("");
  const [importingFb, setImportingFb] = useState(false);
  
  // X/Twitter import
  const [xDialogOpen, setXDialogOpen] = useState(false);
  const [xPostUrl, setXPostUrl] = useState("");
  const [importingX, setImportingX] = useState(false);
  
  const handleImportFbPost = async () => {
    if (!fbPostUrl || !fbPostUrl.includes("facebook.com")) {
      toast.error("Podaj prawidłowy link do posta Facebook");
      return;
    }
    setImportingFb(true);
    try {
      const res = await axios.post(`${API}/rss-feeds/import-facebook-post`, { url: fbPostUrl });
      toast.success(res.data.message);
      setFbDialogOpen(false);
      setFbPostUrl("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Błąd importu");
    } finally {
      setImportingFb(false);
    }
  };

  const handleImportXPost = async () => {
    if (!xPostUrl || (!xPostUrl.includes("x.com") && !xPostUrl.includes("twitter.com"))) {
      toast.error("Podaj prawidłowy link do posta X/Twitter");
      return;
    }
    setImportingX(true);
    try {
      const res = await axios.post(`${API}/rss-feeds/import-x-post`, { url: xPostUrl });
      toast.success(res.data.message);
      if (res.data.post) {
        const p = res.data.post;
        toast.info(`@${p.username} | ${p.replies_count} komentarzy`);
      }
      setXDialogOpen(false);
      setXPostUrl("");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Błąd importu z X");
    } finally {
      setImportingX(false);
    }
  };

  // Dostępne języki źródeł RSS
  const languages = [
    { code: "pl", name: "Polski" },
    { code: "en", name: "Angielski" },
    { code: "de", name: "Niemiecki" },
    { code: "fr", name: "Francuski" },
    { code: "es", name: "Hiszpański" },
    { code: "it", name: "Włoski" },
    { code: "ru", name: "Rosyjski" },
    { code: "uk", name: "Ukraiński" },
    { code: "cs", name: "Czeski" },
    { code: "other", name: "Inny" },
  ];

  const fetchData = useCallback(async () => {
    try {
      const [feedsRes, pendingRes, sitesRes] = await Promise.all([
        axios.get(`${API}/rss-feeds`),
        axios.get(`${API}/rss-feeds/pending${pendingFilter !== "all" ? `?status=${pendingFilter}` : ""}`),
        axios.get(`${API}/sites`),
      ]);
      setFeeds(feedsRes.data);
      setPendingPosts(pendingRes.data);
      setSites(sitesRes.data);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [pendingFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const resetForm = () => {
    setForm({ name: "", url: "", auto_import: false, auto_publish: false, target_mode: "fixed", target_site_ids: [], check_interval: 60, site_id: "", category: "", source_language: "pl", source_type: "rss", import_limit: 10 });
    setEditingFeed(null);
    setTestResult(null);
    setAiSuggestion(null);
  };

  // AI sugeruje stronę dla RSS
  const handleSuggestSite = async () => {
    if (!form.url && !form.name) {
      toast.error("Najpierw podaj URL lub nazwe feeda");
      return;
    }
    setSuggestingSite(true);
    setAiSuggestion(null);
    try {
      const payload = {
        url: form.url,
        feed_title: form.name || testResult?.feed_title || "",
        sample_titles: testResult?.entries_preview?.map(e => e.title) || []
      };
      const res = await axios.post(`${API}/rss-feeds/suggest-site`, payload);
      setAiSuggestion(res.data);
      
      // Automatycznie ustaw sugerowaną stronę
      if (res.data.suggested_site?.id) {
        setForm(f => ({ ...f, site_id: res.data.suggested_site.id }));
        toast.success(`AI sugeruje: ${res.data.suggested_site.name}`);
      } else {
        toast.info("AI nie znalazł pasującej strony");
      }
    } catch (err) { 
      toast.error("Błąd AI sugestii");
      console.error(err);
    }
    finally { setSuggestingSite(false); }
  };

  const handleSave = async () => {
    try {
      const payload = { ...form, site_id: form.site_id || null };
      if (editingFeed) {
        await axios.put(`${API}/rss-feeds/${editingFeed.id}`, payload);
      } else {
        await axios.post(`${API}/rss-feeds`, payload);
      }
      toast.success(t("common.success"));
      fetchData();
      setDialogOpen(false);
      resetForm();
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleEdit = (feed) => {
    setEditingFeed(feed);
    setForm({
      name: feed.name, url: feed.url, auto_import: feed.auto_import, auto_publish: feed.auto_publish || false, target_mode: feed.target_mode || "fixed", target_site_ids: feed.target_site_ids || [],
      check_interval: feed.check_interval, site_id: feed.site_id || "", category: feed.category || "",
      source_language: feed.source_language || "pl", source_type: feed.source_type || "rss",
      import_limit: feed.import_limit || 10,
    });
    setAiSuggestion(null);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/rss-feeds/${id}`);
      toast.success(t("common.success"));
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleTest = async () => {
    if (!form.url) return;
    setTesting(true);
    setTestResult(null);
    try {
      // Dla Facebook i Website nie testuj jako RSS
      if (form.source_type === "facebook") {
        if (form.url.includes("facebook.com")) {
          setTestResult({ success: true, feed_title: "Strona Facebook", entries_count: 0, message: "Link Facebook poprawny" });
          toast.success("Link Facebook poprawny. Zapisz i użyj Importuj.");
        } else {
          setTestResult({ success: false, error: "Podaj prawidłowy link do Facebook" });
          toast.error("Nieprawidłowy link Facebook");
        }
        setTesting(false);
        return;
      }
      
      if (form.source_type === "website") {
        if (form.url.startsWith("http")) {
          setTestResult({ success: true, feed_title: "Strona WWW", entries_count: 0, message: "URL poprawny" });
          toast.success("URL poprawny. Zapisz i użyj Importuj.");
        } else {
          setTestResult({ success: false, error: "Podaj prawidłowy URL (https://...)" });
          toast.error("Nieprawidłowy URL");
        }
        setTesting(false);
        return;
      }
      
      if (form.source_type === "twitter_profile") {
        if (form.url.includes("twitter.com") || form.url.includes("x.com")) {
          setTestResult({ success: true, feed_title: "Profil X/Twitter", entries_count: 0, message: "Link do profilu poprawny" });
          toast.success("Link X/Twitter poprawny. Zapisz i użyj Importuj.");
        } else {
          setTestResult({ success: false, error: "Podaj prawidłowy link do profilu X/Twitter" });
          toast.error("Nieprawidłowy link X/Twitter");
        }
        setTesting(false);
        return;
      }
      
      // Standardowy test RSS
      const res = await axios.post(`${API}/rss-feeds/test`, { url: form.url });
      setTestResult(res.data);
      if (res.data.success && !form.name) {
        setForm(f => ({ ...f, name: res.data.feed_title || f.name }));
      }
      toast[res.data.success ? "success" : "error"](
        res.data.success ? `${t("rssAdv.testOk")}: ${res.data.entries_count} ${t("rssAdv.articles")}` : (res.data.error || t("rssAdv.testFail"))
      );
    } catch (err) { toast.error(t("common.error")); }
    finally { setTesting(false); }
  };


  const handleImport = async (feedId) => {
    setImporting(feedId);
    try {
      // Znajdź feed żeby sprawdzić typ źródła i limit
      const feed = feeds.find(f => f.id === feedId);
      const sourceType = feed?.source_type || "rss";
      const importLimit = feed?.import_limit || 10;
      
      let endpoint = `${API}/rss-feeds/${feedId}/import`;
      let payload = {};
      
      if (sourceType === "website" || sourceType === "facebook") {
        endpoint = `${API}/rss-feeds/${feedId}/scrape-website`;
        payload = { limit: importLimit };
      }
      
      // Dla Twitter/X też użyj scrape-website endpoint
      if (sourceType === "twitter_profile") {
        endpoint = `${API}/rss-feeds/${feedId}/scrape-website`;
        payload = { limit: importLimit };
      }
      
      const res = await axios.post(endpoint, payload);
      toast.success(`${t("rssAdv.imported")}: ${res.data.imported} ${t("rssAdv.articles")}`);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setImporting(null); }
  };

  // Masowe tłumaczenie wszystkich artykułów
  const handleTranslateAll = async () => {
    setTranslating(true);
    try {
      const res = await axios.post(`${API}/rss-feeds/pending/translate-all`);
      toast.success(`Przetłumaczono ${res.data.translated} artykułów`);
      fetchData();
    } catch (err) {
      toast.error("Błąd tłumaczenia: " + (err.response?.data?.detail || err.message));
    } finally { setTranslating(false); }
  };

  // Pobierz zdjęcia dla artykułów bez zdjęć
  const handleFetchImages = async () => {
    setTranslating(true); // użyj tego samego stanu loading
    try {
      const res = await axios.post(`${API}/rss-feeds/pending/fetch-images`);
      toast.success(`Pobrano zdjęcia dla ${res.data.updated} artykułów`);
      fetchData();
    } catch (err) {
      toast.error("Błąd pobierania zdjęć");
    } finally { setTranslating(false); }
  };

  const handleRate = async (postId) => {
    setRatingId(postId);
    try {
      const res = await axios.post(`${API}/rss-feeds/pending/${postId}/rate`);
      toast.success(t("rssAdv.rated"));
      fetchData();
      if (selectedPending?.id === postId) {
        setSelectedPending(p => ({ ...p, ai_rating: res.data }));
      }
    } catch (err) { toast.error(t("common.error")); }
    finally { setRatingId(null); }
  };

  const handlePendingStatus = async (postId, status) => {
    try {
      await axios.patch(`${API}/rss-feeds/pending/${postId}`, { status });
      toast.success(t("common.success"));
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  const handleDeletePending = async (postId) => {
    try {
      await axios.delete(`${API}/rss-feeds/pending/${postId}`);
      if (selectedPending?.id === postId) setSelectedPending(null);
      toast.success(t("common.success"));
      fetchData();
    } catch (err) { toast.error(t("common.error")); }
  };

  return (
    <div data-testid="rss-page" className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">{t("rss.title")}</h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("rss.subtitle")}</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button data-testid="add-feed-btn" className="rounded-lg gap-2">
              <Plus className="w-4 h-4" strokeWidth={1.5} /> {t("rss.addFeed")}
            </Button>
          </DialogTrigger>
          <DialogContent style={{maxWidth: "700px", width: "calc(100vw - 2rem)", maxHeight: "90vh", overflowY: "auto"}}>
            <DialogHeader>
              <DialogTitle className="font-heading">
                {editingFeed ? t("sites.edit") : t("rss.addFeed")}
              </DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              {/* Wybór typu źródła */}
              <div className="space-y-2">
                <Label>Typ źródła</Label>
                <div className="flex gap-2 flex-wrap">
                  <Button 
                    type="button"
                    variant={form.source_type === "rss" ? "default" : "outline"}
                    size="sm"
                    className="flex-1 min-w-[100px]"
                    onClick={() => setForm({ ...form, source_type: "rss" })}
                  >
                    <Rss className="w-4 h-4 mr-1" /> RSS
                  </Button>
                  <Button 
                    type="button"
                    variant={form.source_type === "website" ? "default" : "outline"}
                    size="sm"
                    className="flex-1 min-w-[100px]"
                    onClick={() => setForm({ ...form, source_type: "website" })}
                  >
                    <Globe className="w-4 h-4 mr-1" /> WWW
                  </Button>
                  <Button 
                    type="button"
                    variant={form.source_type === "facebook" ? "default" : "outline"}
                    size="sm"
                    className="flex-1 min-w-[100px]"
                    onClick={() => setForm({ ...form, source_type: "facebook" })}
                  >
                    <ExternalLink className="w-4 h-4 mr-1" /> Facebook
                  </Button>
                  <Button 
                    type="button"
                    variant={form.source_type === "twitter_profile" ? "default" : "outline"}
                    size="sm"
                    className="flex-1 min-w-[100px]"
                    onClick={() => setForm({ ...form, source_type: "twitter_profile" })}
                  >
                    <svg className="w-4 h-4 mr-1" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                    X/Twitter
                  </Button>
                </div>
                {form.source_type === "website" && (
                  <p className="text-[10px] text-amber-600">AI automatycznie znajdzie artykuły na stronie</p>
                )}
                {form.source_type === "facebook" && (
                  <p className="text-[10px] text-blue-600">Podaj link do profilu lub strony FB</p>
                )}
                {form.source_type === "twitter_profile" && (
                  <p className="text-[10px] text-sky-600">Podaj link do profilu X/Twitter np. https://x.com/username</p>
                )}
              </div>
              
              <div className="space-y-2">
                <Label>{form.source_type === "rss" ? t("rss.url") : form.source_type === "twitter_profile" ? "Profil X/Twitter" : "Adres strony"}</Label>
                <div className="flex gap-2">
                  <Input data-testid="feed-url-input" value={form.url} className="flex-1"
                    onChange={(e) => { setForm({ ...form, url: e.target.value }); setTestResult(null); }}
                    placeholder={form.source_type === "rss" ? "https://example.com/rss" : form.source_type === "facebook" ? "https://facebook.com/nazwa-strony" : form.source_type === "twitter_profile" ? "https://x.com/username" : "https://example.com"} />
                  <Button variant="outline" data-testid="test-feed-btn" onClick={handleTest}
                    disabled={testing || !form.url} className="gap-1 shrink-0">
                    {testing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> :
                     <Wifi className="w-3.5 h-3.5" strokeWidth={1.5} />}
                    {t("rssAdv.test")}
                  </Button>
                </div>
              </div>
              {testResult && (
                <div className={`p-3 rounded-lg text-xs ${testResult.success ? "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/20 dark:text-emerald-400" : "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400"}`}>
                  {testResult.success ? (
                    <div>
                      <p className="font-semibold">{testResult.feed_title}</p>
                      <p>{testResult.entries_count} {t("rssAdv.articles")} {t("rssAdv.found")}</p>
                      {testResult.entries_preview?.slice(0, 3).map((e, i) => (
                        <p key={i} className="truncate mt-0.5 opacity-75">- {e.title}</p>
                      ))}
                    </div>
                  ) : <p>{testResult.error}</p>}
                </div>
              )}
              <div className="space-y-2">
                <Label>{t("rss.name")}</Label>
                <Input data-testid="feed-name-input" value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })} />
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>{t("rssAdv.targetSite")}</Label>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="h-7 text-xs gap-1.5 text-purple-600 border-purple-300 hover:bg-purple-50"
                    onClick={handleSuggestSite}
                    disabled={suggestingSite || (!form.url && !form.name)}
                    data-testid="ai-suggest-site-btn"
                  >
                    {suggestingSite ? (
                      <Loader2 className="w-3 h-3 animate-spin" />
                    ) : (
                      <Wand2 className="w-3 h-3" />
                    )}
                    AI sugestia
                  </Button>
                </div>
                <Select value={form.site_id || "none"} onValueChange={(v) => setForm({ ...form, site_id: v === "none" ? "" : v })}>
                  <SelectTrigger className="h-9 text-xs"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none" className="text-xs">—</SelectItem>
                    {sites.map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                
                {/* AI Suggestion Result */}
                {aiSuggestion && (
                  <div className="p-3 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800 mt-2">
                    <div className="flex items-start gap-2">
                      <Wand2 className="w-4 h-4 text-purple-600 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="text-xs font-semibold text-purple-700 dark:text-purple-300">
                          {aiSuggestion.suggested_site?.name || "Brak sugestii"}
                        </p>
                        <p className="text-[10px] text-purple-600/80 dark:text-purple-400/80 mt-0.5">
                          {aiSuggestion.reasoning}
                        </p>
                        {aiSuggestion.confidence && (
                          <Badge variant="outline" className="text-[9px] mt-1 border-purple-300 text-purple-600">
                            Pewność: {aiSuggestion.confidence}%
                          </Badge>
                        )}
                        {aiSuggestion.alternatives?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-purple-200 dark:border-purple-700">
                            <p className="text-[9px] text-muted-foreground mb-1">Alternatywy:</p>
                            <div className="flex flex-wrap gap-1">
                              {aiSuggestion.alternatives.map((alt, i) => (
                                <Button 
                                  key={i} 
                                  variant="ghost" 
                                  size="sm" 
                                  className="h-6 text-[10px] px-2"
                                  onClick={() => setForm(f => ({ ...f, site_id: alt.id }))}
                                >
                                  {alt.name}
                                </Button>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-2">
                  <Label>{t("rss.interval")}</Label>
                  <Input data-testid="interval-input" type="number" value={form.check_interval}
                    onChange={(e) => setForm({ ...form, check_interval: parseInt(e.target.value) || 60 })} />
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <span>Język źródła</span>
                    <span className="text-[9px] text-muted-foreground">(do tłumaczenia)</span>
                  </Label>
                  <Select value={form.source_language} onValueChange={(v) => setForm({ ...form, source_language: v })}>
                    <SelectTrigger className="h-9 text-xs" data-testid="source-language-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {languages.map(lang => (
                        <SelectItem key={lang.code} value={lang.code} className="text-xs">{lang.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label className="flex items-center gap-1.5">
                    <span>Limit importu</span>
                    <span className="text-[9px] text-muted-foreground">(na sesję)</span>
                  </Label>
                  <Select value={String(form.import_limit || 10)} onValueChange={(v) => setForm({ ...form, import_limit: parseInt(v) })}>
                    <SelectTrigger className="h-9 text-xs" data-testid="import-limit-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="2" className="text-xs">2 artykuły</SelectItem>
                      <SelectItem value="5" className="text-xs">5 artykułów</SelectItem>
                      <SelectItem value="10" className="text-xs">10 artykułów</SelectItem>
                      <SelectItem value="20" className="text-xs">20 artykułów</SelectItem>
                      <SelectItem value="50" className="text-xs">50 artykułów</SelectItem>
                      <SelectItem value="100" className="text-xs">100 artykułów</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2 flex items-end">
                  <div className="flex items-center gap-2 h-9 px-3 border rounded-md bg-muted/30">
                    <Label className="text-xs">{t("rss.autoImport")}</Label>
                    <Switch data-testid="auto-import-switch" checked={form.auto_import}
                      onCheckedChange={(v) => setForm({ ...form, auto_import: v })} />
                  </div>
                </div>
              </div>
              {form.source_language && form.source_language !== "pl" && (
                <div className="p-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
                  <p className="text-xs text-amber-700 dark:text-amber-300 flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" />
                    AI automatycznie przetłumaczy treści na język strony docelowej
                  </p>
                </div>
              )}

              {/* === AUTO-PUBLIKACJA RSS === */}
              <div className="p-4 rounded-lg border-2 border-orange-300 bg-orange-50 dark:bg-orange-900/20 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-base font-bold text-orange-700 dark:text-orange-300">Auto-publikacja RSS</p>
                    <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">Agent automatycznie pobiera, przeredagowuje i publikuje artykuly z tego kanalu RSS</p>
                  </div>
                  <Switch checked={form.auto_publish || false}
                    onCheckedChange={(v) => setForm({ ...form, auto_publish: v })} />
                </div>

                <div>
                  <p className="text-sm font-bold mb-2">Tryb publikacji:</p>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button"
                      className={`p-4 rounded-lg border-2 text-left ${form.target_mode === "fixed" ? "border-orange-500 bg-white dark:bg-orange-900/30" : "border-gray-200 dark:border-gray-700 hover:border-orange-300"}`}
                      onClick={() => setForm({...form, target_mode: "fixed"})}>
                      <div className="text-sm font-bold">{form.target_mode === "fixed" ? "\u2713 " : ""}Przypisane strony</div>
                      <div className="text-xs text-muted-foreground mt-1">Wybierasz na jakie strony publikowac</div>
                    </button>
                    <button type="button"
                      className={`p-4 rounded-lg border-2 text-left ${form.target_mode === "auto" ? "border-blue-500 bg-white dark:bg-blue-900/30" : "border-gray-200 dark:border-gray-700 hover:border-blue-300"}`}
                      onClick={() => setForm({...form, target_mode: "auto"})}>
                      <div className="text-sm font-bold">{form.target_mode === "auto" ? "\u2713 " : ""}Agent decyduje</div>
                      <div className="text-xs text-muted-foreground mt-1">AI wybiera strone po kategorii tematu</div>
                    </button>
                  </div>
                </div>

                {form.target_mode === "fixed" && (
                  <div>
                    <p className="text-sm font-bold mb-2">Wybierz strony docelowe:</p>
                    <div className="grid grid-cols-3 gap-2 max-h-[250px] overflow-y-auto p-3 bg-white dark:bg-gray-900 rounded-lg border">
                      {sites.filter(s => s.status === "active").map(site => (
                        <button key={site.id} type="button"
                          className={`text-xs px-3 py-2 rounded-md border text-left truncate ${(form.target_site_ids || []).includes(site.id) ? "bg-orange-500 text-white border-orange-600 font-bold" : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-orange-50 dark:bg-gray-800 dark:text-gray-300"}`}
                          onClick={() => {
                            const ids = (form.target_site_ids || []);
                            const newIds = ids.includes(site.id) ? ids.filter(id => id !== site.id) : [...ids, site.id];
                            setForm({...form, target_site_ids: newIds});
                          }}>
                          {(form.target_site_ids || []).includes(site.id) ? "\u2713 " : ""}{site.name}
                        </button>
                      ))}
                    </div>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-sm font-bold text-orange-600">{(form.target_site_ids || []).length} stron wybranych</p>
                      <p className="text-xs text-muted-foreground italic">Kazda strona dostanie osobny przeredagowany tekst</p>
                    </div>
                  </div>
                )}

                {form.target_mode === "auto" && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-700 dark:text-blue-300"><strong>Agent AI</strong> sam wybierze strone na podstawie kategorii tematu (sport, polityka, finanse itp.)</p>
                  </div>
                )}
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>{t("rss.cancel")}</Button>
              <Button data-testid="save-feed-btn" onClick={handleSave}>{t("rss.save")}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList data-testid="rss-tabs">
          <TabsTrigger value="feeds" className="gap-1.5">
            <Rss className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t("rssAdv.feedsTab")}
            <Badge variant="secondary" className="text-[10px] rounded-md ml-1">{feeds.length}</Badge>
          </TabsTrigger>
          <TabsTrigger value="pending" className="gap-1.5">
            <FileText className="w-3.5 h-3.5" strokeWidth={1.5} />
            {t("rssAdv.pendingTab")}
            <Badge variant="secondary" className="text-[10px] rounded-md ml-1">
              {pendingPosts.filter(p => p.status === "pending").length}
            </Badge>
          </TabsTrigger>
        </TabsList>

        {/* ===== FEEDS TAB ===== */}
        <TabsContent value="feeds" className="mt-4">
          <Card className="rounded-xl border shadow-sm bg-card">
            <CardContent className="p-0">
              {feeds.length === 0 ? (
                <div data-testid="no-feeds" className="flex flex-col items-center justify-center py-16 text-muted-foreground">
                  <Rss className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                  <p className="text-xs">{t("rss.noFeeds")}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-xs font-semibold">{t("rss.name")}</TableHead>
                      <TableHead className="text-xs font-semibold">{t("rss.url")}</TableHead>
                      <TableHead className="text-xs font-semibold">Język</TableHead>
                      <TableHead className="text-xs font-semibold">{t("rss.autoImport")}</TableHead>
                      <TableHead className="text-xs font-semibold">Limit</TableHead>
                      <TableHead className="text-xs font-semibold">{t("rss.imported")}</TableHead>
                      <TableHead className="text-xs font-semibold">{t("rssAdv.lastCheck")}</TableHead>
                      <TableHead className="text-xs font-semibold text-right">{t("sites.actions")}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {feeds.map((feed) => (
                      <TableRow key={feed.id} data-testid={`feed-row-${feed.id}`}>
                        <TableCell className="font-medium text-xs flex items-center gap-1.5">
                          {feed.source_type === "website" ? <Globe className="w-3.5 h-3.5 text-blue-500" /> : 
                           feed.source_type === "facebook" ? <ExternalLink className="w-3.5 h-3.5 text-blue-600" /> : 
                           feed.source_type === "twitter_profile" ? <svg className="w-3.5 h-3.5 text-sky-500" viewBox="0 0 24 24" fill="currentColor"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg> : 
                           <Rss className="w-3.5 h-3.5 text-orange-500" />}
                          {feed.name}
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-[180px] truncate">{feed.url}</TableCell>
                        <TableCell>
                          <Badge variant="outline" className="text-[10px] uppercase">
                            {feed.source_language || "pl"}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge className={`text-[10px] border-0 rounded-md ${feed.auto_import ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" : "bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-500"}`}>
                            {feed.auto_import ? "ON" : "OFF"}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs">{feed.import_limit || 10}</TableCell>
                        <TableCell className="text-xs">{feed.items_imported || 0}</TableCell>
                        <TableCell className="text-[10px] text-muted-foreground">
                          {feed.last_checked ? new Date(feed.last_checked).toLocaleString() : "—"}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1">
                            <Button variant="outline" size="sm" className="h-7 text-[10px] gap-1"
                              data-testid={`import-feed-${feed.id}`}
                              onClick={() => handleImport(feed.id)}
                              disabled={importing === feed.id}>
                              {importing === feed.id ? <Loader2 className="w-3 h-3 animate-spin" /> :
                               <Download className="w-3 h-3" strokeWidth={1.5} />}
                              {t("rssAdv.import")}
                            </Button>
                            <Button size="sm" variant={feed.auto_publish ? "default" : "outline"}
                              className={`h-7 text-[10px] px-2 ${feed.auto_publish ? "bg-orange-500 hover:bg-orange-600 text-white" : ""}`}
                              onClick={() => setAutoPubFeed({...feed, target_site_ids: feed.target_site_ids || [], target_mode: feed.target_mode || "fixed"})}>
                              {feed.auto_publish ? "AUTO PUB" : "Auto pub"}
                            </Button>
                            {feed.auto_publish && (
                              <Button size="sm" variant="outline"
                                className="h-7 text-[10px] px-2 border-green-400 text-green-700 hover:bg-green-50"
                                disabled={runningAuto === feed.id}
                                onClick={() => handleRunAutoNow(feed.id, feed.name)}>
                                {runningAuto === feed.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <RefreshCw className="w-3 h-3" />}
                              </Button>
                            )}
                            <Button variant="ghost" size="icon" onClick={() => handleEdit(feed)} className="h-7 w-7">
                              <Pencil className="w-3 h-3" strokeWidth={1.5} />
                            </Button>
                            <Button variant="ghost" size="icon" onClick={() => handleDelete(feed.id)}
                              className="h-7 w-7 text-destructive"><Trash2 className="w-3 h-3" strokeWidth={1.5} /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== PENDING POSTS TAB ===== */}
        <TabsContent value="pending" className="mt-4">
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            <Select value={pendingFilter} onValueChange={setPendingFilter}>
              <SelectTrigger data-testid="pending-filter" className="h-8 text-xs w-[140px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">{t("queue.all")}</SelectItem>
                <SelectItem value="pending" className="text-xs">{t("rssAdv.statusPending")}</SelectItem>
                <SelectItem value="approved" className="text-xs">{t("rssAdv.statusApproved")}</SelectItem>
                <SelectItem value="published" className="text-xs">Opublikowane</SelectItem>
                <SelectItem value="rejected" className="text-xs">{t("rssAdv.statusRejected")}</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Przycisk masowego tłumaczenia */}
            <Button 
              variant="outline" 
              size="sm" 
              className="h-8 text-xs gap-1.5 border-blue-300 text-blue-600 hover:bg-blue-50"
              onClick={handleTranslateAll}
              disabled={translating || pendingPosts.length === 0}
              data-testid="translate-all-btn"
            >
              {translating ? (
                <Loader2 className="w-3.5 h-3.5 animate-spin" />
              ) : (
                <Globe className="w-3.5 h-3.5" />
              )}
              {translating ? "Przetwarzanie..." : "Przetłumacz wszystkie"}
            </Button>
            
            {/* Przycisk importu posta FB */}
            <Button 
              variant="outline" 
              size="sm" 
              className="h-8 text-xs gap-1.5 border-blue-300 text-blue-600 hover:bg-blue-50"
              onClick={() => setFbDialogOpen(true)}
              data-testid="import-fb-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Import z FB
            </Button>
            
            {/* Przycisk importu posta X/Twitter */}
            <Button 
              variant="outline" 
              size="sm" 
              className="h-8 text-xs gap-1.5 border-gray-700 text-gray-900 hover:bg-gray-100 dark:border-gray-500 dark:text-gray-100 dark:hover:bg-gray-800"
              onClick={() => setXDialogOpen(true)}
              data-testid="import-x-btn"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              Import z X
            </Button>
            
            {/* Przycisk pobierania zdjęć */}
            <Button 
              variant="outline" 
              size="sm" 
              className="h-8 text-xs gap-1.5 border-purple-300 text-purple-600 hover:bg-purple-50"
              onClick={handleFetchImages}
              disabled={translating || pendingPosts.length === 0}
              data-testid="fetch-images-btn"
            >
              <ImageIcon className="w-3.5 h-3.5" />
              Pobierz zdjęcia
            </Button>
            
            {/* Liczniki */}
            {pendingPosts.filter(p => !p.title_pl).length > 0 && (
              <Badge variant="outline" className="text-[10px] border-amber-300 text-amber-600">
                {pendingPosts.filter(p => !p.title_pl).length} do przetłumaczenia
              </Badge>
            )}
            {pendingPosts.filter(p => !p.image_url).length > 0 && (
              <Badge variant="outline" className="text-[10px] border-purple-300 text-purple-600">
                {pendingPosts.filter(p => !p.image_url).length} bez zdjęcia
              </Badge>
            )}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className={selectedPending ? "lg:col-span-1" : "lg:col-span-3"}>
              <Card className="rounded-xl border shadow-sm bg-card">
                <CardContent className="p-0">
                  {pendingPosts.length === 0 ? (
                    <div data-testid="no-pending" className="flex flex-col items-center justify-center py-12 text-muted-foreground">
                      <FileText className="w-10 h-10 mb-3 opacity-20" strokeWidth={1} />
                      <p className="text-xs">{t("rssAdv.noPending")}</p>
                    </div>
                  ) : (
                    <ScrollArea className="h-[calc(100vh-280px)] overflow-y-auto">
                      <div className="divide-y divide-border">
                        {pendingPosts.map((post) => (
                          <button key={post.id} data-testid={`pending-${post.id}`}
                            onClick={() => {
                            setSelectedPending(post);
                            const suggestedSite = post.ai_rating?.suggested_site_id || post.suggested_site_id || "";
                            setPublishSiteId(suggestedSite);
                            if (suggestedSite) loadWpCategories(suggestedSite);
                          }}
                            className={`w-full flex items-start gap-3 p-3 text-left transition-all hover:bg-accent/30 ${
                              selectedPending?.id === post.id ? "bg-primary/5" : ""
                            }`}>
                            {/* Ikona zdjęcia */}
                            {post.image_url ? (
                              <div className="w-8 h-8 rounded-md bg-emerald-500 flex items-center justify-center shrink-0" title="Zdjęcie dostępne">
                                <ImageIcon className="w-4 h-4 text-white" />
                              </div>
                            ) : (
                              <div className="w-8 h-8 rounded-md bg-gray-200 dark:bg-gray-700 flex items-center justify-center shrink-0" title="Brak zdjęcia">
                                <ImageIcon className="w-4 h-4 text-gray-400" />
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              {/* Pokaż przetłumaczony tytuł jeśli dostępny */}
                              <p className={`text-xs font-medium leading-snug truncate ${post.status === "published" ? "text-muted-foreground" : "text-foreground"}`}>
                                {post.ai_rating?.title_pl || post.title_pl || post.title}
                              </p>
                              {/* Oryginalny tytuł jeśli inny */}
                              {post.ai_rating?.title_pl && post.ai_rating.title_pl !== post.title && (
                                <p className="text-[10px] text-muted-foreground truncate mt-0.5 italic">
                                  {post.title}
                                </p>
                              )}
                              <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                                <Badge variant="outline" className="text-[9px] rounded-md">{post.source_feed_name}</Badge>
                                {post.source_language && post.source_language !== "pl" && (
                                  <Badge variant="outline" className="text-[9px] rounded-md uppercase">
                                    {post.source_language}
                                  </Badge>
                                )}
                                {/* Status opublikowany */}
                                {post.status === "published" && (
                                  <Badge className="text-[8px] border-0 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400">
                                    <CheckCircle2 className="w-2.5 h-2.5 mr-0.5" /> opublikowany
                                  </Badge>
                                )}
                                {post.ai_rating?.tags?.slice(0, 2).map((tag, i) => (
                                  <Badge key={i} variant="outline" className={`text-[8px] ${
                                    tag === "goracy_temat" ? "border-red-300 text-red-600" :
                                    tag === "klikalny" ? "border-purple-300 text-purple-600" :
                                    tag === "breaking_news" ? "border-orange-300 text-orange-600" :
                                    tag === "slaby" ? "border-slate-300 text-slate-400" :
                                    "border-slate-200"
                                  }`}>
                                    {tag.replace(/_/g, " ")}
                                  </Badge>
                                ))}
                              </div>
                            </div>
                            {post.ai_rating && (
                              <div className={`flex items-center justify-center w-8 h-8 rounded-lg font-bold text-sm shrink-0 ${
                                post.status === "published" ? "bg-emerald-100 text-emerald-700" :
                                (post.ai_rating.score || post.ai_rating.quality_score) >= 8 ? "bg-emerald-100 text-emerald-700" :
                                (post.ai_rating.score || post.ai_rating.quality_score) >= 6 ? "bg-amber-100 text-amber-700" :
                                (post.ai_rating.score || post.ai_rating.quality_score) >= 4 ? "bg-slate-100 text-slate-600" :
                                "bg-red-100 text-red-600"
                              }`}>
                                {post.status === "published" ? "✓" : (post.ai_rating.score || post.ai_rating.quality_score || "?")}
                              </div>
                            )}
                          </button>
                        ))}
                      </div>
                    </ScrollArea>
                  )}
                </CardContent>
              </Card>
            </div>

            {/* Pending post detail */}
            {selectedPending && (
              <div className="lg:col-span-2">
                <Card data-testid="pending-detail" className="rounded-xl border shadow-sm bg-card">
                  <CardHeader className="pb-2 pt-4 px-5">
                    <div className="flex items-start justify-between">
                      <CardTitle className="text-sm font-heading text-foreground">{selectedPending.title}</CardTitle>
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive shrink-0"
                        onClick={() => handleDeletePending(selectedPending.id)}>
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent className="px-5 pb-5 pt-2 space-y-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] rounded-md">{selectedPending.source_feed_name}</Badge>
                      {selectedPending.source_link && (
                        <a href={selectedPending.source_link} target="_blank" rel="noopener noreferrer"
                          className="text-[10px] text-primary hover:underline flex items-center gap-0.5">
                          {t("rssAdv.original")} <ExternalLink className="w-2.5 h-2.5" />
                        </a>
                      )}
                      {selectedPending.published_at && (
                        <span className="text-[10px] text-muted-foreground">{selectedPending.published_at}</span>
                      )}
                    </div>

                    {selectedPending.summary && (
                      <div className="p-3 rounded-lg bg-muted/50">
                        <p className="text-xs text-foreground/80 leading-relaxed">{selectedPending.summary}</p>
                      </div>
                    )}

                    {/* AI Rating */}
                    <div className="flex items-center justify-between">
                      <p className="text-xs font-semibold text-foreground">{t("rssAdv.aiRating")}</p>
                      <Button size="sm" data-testid="rate-pending-btn"
                        onClick={() => handleRate(selectedPending.id)}
                        disabled={ratingId === selectedPending.id}
                        className="h-7 text-xs gap-1.5 rounded-md">
                        {ratingId === selectedPending.id ? <Loader2 className="w-3 h-3 animate-spin" /> :
                         <Sparkles className="w-3 h-3" strokeWidth={1.5} />}
                        {t("rssAdv.rateArticle")}
                      </Button>
                    </div>

                    {selectedPending.ai_rating && (
                      <div className="space-y-3">
                        {/* Score i tagi */}
                        <div className="flex items-center gap-3">
                          <div className={`flex items-center justify-center w-14 h-14 rounded-xl font-bold text-2xl ${
                            selectedPending.ai_rating.score >= 8 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" :
                            selectedPending.ai_rating.score >= 6 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                            selectedPending.ai_rating.score >= 4 ? "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400" :
                            "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400"
                          }`}>
                            {selectedPending.ai_rating.score || selectedPending.ai_rating.quality_score || "?"}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5 flex-wrap mb-1">
                              {selectedPending.ai_rating.importance && (
                                <Badge className={`text-[10px] border-0 ${
                                  selectedPending.ai_rating.importance === "krytyczna" ? "bg-red-500 text-white" :
                                  selectedPending.ai_rating.importance === "wysoka" ? "bg-orange-500 text-white" :
                                  selectedPending.ai_rating.importance === "srednia" ? "bg-blue-500 text-white" :
                                  "bg-slate-400 text-white"
                                }`}>
                                  {selectedPending.ai_rating.importance}
                                </Badge>
                              )}
                              {selectedPending.ai_rating.tags?.map((tag, i) => (
                                <Badge key={i} variant="outline" className={`text-[9px] ${
                                  tag === "goracy_temat" ? "border-red-300 text-red-600" :
                                  tag === "klikalny" ? "border-purple-300 text-purple-600" :
                                  tag === "breaking_news" ? "border-orange-300 text-orange-600" :
                                  tag === "evergreen" ? "border-green-300 text-green-600" :
                                  tag === "praktyczny" ? "border-blue-300 text-blue-600" :
                                  tag === "kontrowersyjny" ? "border-pink-300 text-pink-600" :
                                  tag === "slaby" ? "border-slate-300 text-slate-500" :
                                  "border-slate-200"
                                }`}>
                                  {tag.replace(/_/g, " ")}
                                </Badge>
                              ))}
                            </div>
                            {selectedPending.ai_rating.reasoning && (
                              <p className="text-[10px] text-muted-foreground leading-snug">
                                {selectedPending.ai_rating.reasoning}
                              </p>
                            )}
                          </div>
                        </div>

                        {/* Przetłumaczony tytuł */}
                        {selectedPending.ai_rating.title_pl && selectedPending.ai_rating.title_pl !== selectedPending.title && (
                          <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
                            <p className="text-[10px] text-blue-600 dark:text-blue-400 font-medium mb-1 flex items-center gap-1">
                              <Globe className="w-3 h-3" /> Tytuł po polsku:
                            </p>
                            <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                              {selectedPending.ai_rating.title_pl}
                            </p>
                          </div>
                        )}

                        {/* Streszczenie po polsku */}
                        {selectedPending.ai_rating.summary_pl && (
                          <div className="p-3 rounded-lg bg-slate-50 dark:bg-slate-800/50 border border-slate-200 dark:border-slate-700">
                            <p className="text-[10px] text-muted-foreground font-medium mb-1">Streszczenie:</p>
                            <p className="text-xs text-foreground leading-relaxed">
                              {selectedPending.ai_rating.summary_pl}
                            </p>
                          </div>
                        )}

                        {/* Propozycja tytułu artykułu */}
                        {selectedPending.ai_rating.suggested_article_title && (
                          <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
                            <p className="text-[10px] text-emerald-600 dark:text-emerald-400 font-medium mb-1 flex items-center gap-1">
                              <PenTool className="w-3 h-3" /> Propozycja tytułu artykułu:
                            </p>
                            <p className="text-sm font-semibold text-emerald-800 dark:text-emerald-200">
                              {selectedPending.ai_rating.suggested_article_title}
                            </p>
                          </div>
                        )}

                        {/* Sugerowany serwis */}
                        {selectedPending.ai_rating.suggested_site_name && (
                          <div className="flex items-center gap-2 p-2 rounded-lg bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800">
                            <Globe className="w-4 h-4 text-purple-600" />
                            <div className="flex-1">
                              <p className="text-[10px] text-purple-600 dark:text-purple-400">Sugerowany serwis:</p>
                              <p className="text-xs font-medium text-purple-800 dark:text-purple-200">
                                {selectedPending.ai_rating.suggested_site_name}
                              </p>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    <Separator />

                    {/* === ZATWIERDZ I PUBLIKUJ W TLE === */}
                    {selectedPending.status !== "published" && (
                      <div className="p-3 rounded-lg border-2 border-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 space-y-2">
                        <p className="text-xs font-bold text-emerald-800 dark:text-emerald-300">Zatwierdz i publikuj w tle:</p>
                        <div className="flex items-center gap-2">
                          <Select value={publishSiteId || "none"} onValueChange={(v) => { setPublishSiteId(v === "none" ? "" : v); loadWpCategories(v === "none" ? "" : v); }}>
                            <SelectTrigger className="h-8 text-xs flex-1">
                              <SelectValue placeholder="Wybierz strone..." />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="none" className="text-xs">-- wybierz strone --</SelectItem>
                              {sites.map(s => <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>)}
                            </SelectContent>
                          </Select>
                          {loadingCats ? (
                            <div className="flex items-center gap-1 text-[10px] text-muted-foreground"><Loader2 className="w-3 h-3 animate-spin" /> Laduje kategorie...</div>
                          ) : publishCats.length > 0 && (
                            <Select value={publishCatId || "none"} onValueChange={(v) => setPublishCatId(v === "none" ? "" : v)}>
                              <SelectTrigger className="h-8 text-xs flex-1">
                                <SelectValue placeholder="Kategoria..." />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="none" className="text-xs">-- bez kategorii --</SelectItem>
                                {publishCats.map(c => <SelectItem key={c.id} value={String(c.id)} className="text-xs">{c.name} ({c.count || 0})</SelectItem>)}
                              </SelectContent>
                            </Select>
                          )}
                          <Button
                            size="sm"
                            disabled={publishing || !publishSiteId}
                            className="h-8 text-xs gap-1.5 bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                            onClick={() => handleApproveAndPublish(selectedPending.id)}
                          >
                            {publishing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ArrowRight className="w-3.5 h-3.5" />}
                            Do kolejki
                          </Button>
                        </div>
                        {selectedPending.ai_rating?.suggested_site_name && (
                          <p className="text-[10px] text-emerald-600">AI sugeruje: {selectedPending.ai_rating.suggested_site_name}</p>
                        )}
                      </div>
                    )}

                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Przycisk Wygeneruj artykuł */}
                      {selectedPending.ai_rating && (
                        <Button 
                          variant="default" 
                          size="sm" 
                          className="h-8 text-xs rounded-md gap-1.5 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700"
                          onClick={() => {
                            const trendData = {
                              title: selectedPending.ai_rating.suggested_article_title || selectedPending.ai_rating.title_pl || selectedPending.title,
                              description: selectedPending.ai_rating.summary_pl || selectedPending.summary,
                              source_language: selectedPending.source_language || "pl",
                              ai_suggested_site_id: selectedPending.ai_rating.suggested_site_id || selectedPending.suggested_site_id,
                              source_urls: selectedPending.source_link,
                              original_title: selectedPending.title,
                              pending_post_id: selectedPending.id,
                              // Dodatkowe dane dla generatora
                              source_feed_name: selectedPending.source_feed_name,
                              original_content: selectedPending.content || selectedPending.summary,
                              image_url: selectedPending.image_url,
                              categories: selectedPending.categories,
                            };
                            sessionStorage.setItem("pendingTrendData", JSON.stringify(trendData));
                            window.location.href = "/generator?fromRss=1";
                          }}
                        >
                          <Sparkles className="w-3.5 h-3.5" />
                          Wygeneruj artykuł
                        </Button>
                      )}
                      
                      {/* Info jeśli już opublikowany */}
                      {selectedPending.status === "published" && (
                        <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200">
                          <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                          <span className="text-xs text-emerald-700 dark:text-emerald-300">
                            Artykuł został opublikowany
                          </span>
                        </div>
                      )}
                      
                      {selectedPending.status === "pending" && (
                        <>
                          <Button variant="outline" size="sm" className="h-8 text-xs rounded-md text-emerald-600"
                            data-testid="approve-pending"
                            onClick={() => handlePendingStatus(selectedPending.id, "approved")}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> {t("rssAdv.approve")}
                          </Button>
                          <Button variant="outline" size="sm" className="h-8 text-xs rounded-md text-red-500"
                            onClick={() => handlePendingStatus(selectedPending.id, "rejected")}>
                            <XCircle className="w-3 h-3 mr-1" /> {t("rssAdv.reject")}
                          </Button>
                        </>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </TabsContent>
      </Tabs>
      
      {/* Dialog importu posta Facebook */}
      <Dialog open={fbDialogOpen} onOpenChange={setFbDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-blue-600" />
              Import posta z Facebook
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Link do posta Facebook</Label>
              <Input 
                value={fbPostUrl}
                onChange={(e) => setFbPostUrl(e.target.value)}
                placeholder="https://facebook.com/nazwa/posts/..."
                className="text-sm"
              />
              <p className="text-[10px] text-muted-foreground">
                Wklej link do posta, np. https://facebook.com/zywiec112/posts/...
              </p>
            </div>
            <Button 
              onClick={handleImportFbPost} 
              disabled={importingFb || !fbPostUrl}
              className="w-full"
            >
              {importingFb ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importowanie...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Importuj post</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Modal importu z X/Twitter */}
      <Dialog open={xDialogOpen} onOpenChange={setXDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ExternalLink className="w-5 h-5 text-gray-900 dark:text-white" />
              Import posta z X/Twitter
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Link do posta X/Twitter</Label>
              <Input 
                value={xPostUrl}
                onChange={(e) => setXPostUrl(e.target.value)}
                placeholder="https://x.com/username/status/..."
                className="text-sm"
              />
              <p className="text-[10px] text-muted-foreground">
                Wklej link do posta, np. https://x.com/JanMolskiIII/status/123456789
              </p>
            </div>
            <div className="p-3 bg-blue-50 dark:bg-blue-950 rounded-lg">
              <p className="text-xs text-blue-700 dark:text-blue-300">
                <strong>Co zostanie zaimportowane:</strong>
              </p>
              <ul className="text-[10px] text-blue-600 dark:text-blue-400 mt-1 space-y-0.5">
                <li>• Treść głównego tweeta</li>
                <li>• Zdjęcie/grafika (jeśli jest)</li>
                <li>• Komentarze/odpowiedzi (do 20)</li>
              </ul>
            </div>
            <Button 
              onClick={handleImportXPost} 
              disabled={importingX || !xPostUrl}
              className="w-full bg-gray-900 hover:bg-gray-800 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
            >
              {importingX ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Importowanie z X...</>
              ) : (
                <><Download className="w-4 h-4 mr-2" /> Importuj post z X</>
              )}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    

      {/* === AUTO-PUBLIKACJA RSS DIALOG === */}
      <Dialog open={!!autoPubFeed} onOpenChange={(v) => !v && setAutoPubFeed(null)}>
        <DialogContent style={{maxWidth: "800px", width: "calc(100vw - 2rem)", maxHeight: "85vh", overflowY: "auto"}}>
          <DialogHeader>
            <DialogTitle>Auto-publikacja: {autoPubFeed?.name}</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-2">
            <div className="flex items-center justify-between p-4 rounded-lg bg-orange-50 dark:bg-orange-900/20 border border-orange-300">
              <div>
                <h3 className="font-bold text-orange-800 dark:text-orange-300">Automatyczna publikacja</h3>
                <p className="text-xs text-orange-600 dark:text-orange-400 mt-0.5">Agent pobiera, przeredagowuje i publikuje artykuly z tego kanalu</p>
              </div>
              <Switch checked={autoPubFeed?.auto_publish || false}
                onCheckedChange={(v) => setAutoPubFeed({...autoPubFeed, auto_publish: v})} />
            </div>

            {autoPubFeed?.auto_publish && (
              <>
                <div>
                  <Label className="font-bold mb-2 block">Tryb publikacji:</Label>
                  <div className="grid grid-cols-2 gap-3">
                    <button type="button"
                      className={`p-4 rounded-lg border-2 text-left ${autoPubFeed?.target_mode === "fixed" ? "border-orange-500 bg-orange-50 dark:bg-orange-900/30" : "border-gray-200 dark:border-gray-700 hover:border-orange-300"}`}
                      onClick={() => setAutoPubFeed({...autoPubFeed, target_mode: "fixed"})}>
                      <div className="font-bold">{autoPubFeed?.target_mode === "fixed" ? "\u2713 " : ""}Przypisane strony</div>
                      <div className="text-xs text-muted-foreground mt-1">Ty wybierasz na jakie strony publikowac</div>
                    </button>
                    <button type="button"
                      className={`p-4 rounded-lg border-2 text-left ${autoPubFeed?.target_mode === "auto" ? "border-blue-500 bg-blue-50 dark:bg-blue-900/30" : "border-gray-200 dark:border-gray-700 hover:border-blue-300"}`}
                      onClick={() => setAutoPubFeed({...autoPubFeed, target_mode: "auto"})}>
                      <div className="font-bold">{autoPubFeed?.target_mode === "auto" ? "\u2713 " : ""}Agent decyduje</div>
                      <div className="text-xs text-muted-foreground mt-1">AI wybiera strone po kategorii tematu</div>
                    </button>
                  </div>
                </div>

                {autoPubFeed?.target_mode === "fixed" && (
                  <div>
                    <Label className="font-bold mb-2 block">Wybierz strony docelowe:</Label>
                    <div className="grid grid-cols-3 gap-2 max-h-[250px] overflow-y-auto p-3 bg-white dark:bg-gray-900 rounded-lg border">
                      {sites.filter(s => s.status === "active").map(site => (
                        <button key={site.id} type="button"
                          className={`text-xs px-3 py-2 rounded-md border text-left truncate ${(autoPubFeed?.target_site_ids || []).includes(site.id) ? "bg-orange-500 text-white border-orange-600 font-bold" : "bg-gray-50 text-gray-700 border-gray-200 hover:bg-orange-50 dark:bg-gray-800 dark:text-gray-300"}`}
                          onClick={() => {
                            const ids = (autoPubFeed?.target_site_ids || []);
                            const newIds = ids.includes(site.id) ? ids.filter(id => id !== site.id) : [...ids, site.id];
                            setAutoPubFeed({...autoPubFeed, target_site_ids: newIds});
                          }}>
                          {(autoPubFeed?.target_site_ids || []).includes(site.id) ? "\u2713 " : ""}{site.name}
                        </button>
                      ))}
                    </div>
                    <p className="text-sm font-bold text-orange-600 mt-2">{(autoPubFeed?.target_site_ids || []).length} stron wybranych</p>
                  </div>
                )}

                {autoPubFeed?.target_mode === "auto" && (
                  <div className="p-3 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-200">
                    <strong>Agent AI</strong> sam wybierze strone na podstawie kategorii tematu (sport, polityka, finanse itp.)
                  </div>
                )}

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs mb-1 block">Interwal sprawdzania (minuty):</Label>
                    <Input type="number" value={autoPubFeed?.check_interval || 60} 
                      onChange={(e) => setAutoPubFeed({...autoPubFeed, check_interval: parseInt(e.target.value) || 60})} />
                  </div>
                </div>
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setAutoPubFeed(null)}>Anuluj</Button>
            <Button className="bg-orange-600 hover:bg-orange-700" onClick={async () => {
              try {
                await axios.post(`${API}/rss-feeds/${autoPubFeed.id}/set-auto-publish`, {
                  auto_publish: autoPubFeed.auto_publish,
                  target_mode: autoPubFeed.target_mode,
                  target_site_ids: autoPubFeed.target_site_ids,
                  check_interval: autoPubFeed.check_interval,
                });
                setAutoPubFeed(null);
                fetchData();
              } catch {}
            }}>Zapisz</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

</div>
  );
}
